import { PanelContent } from "../../lib/panel/panel-content";
import { useTranslation } from "react-i18next";
import { FormField } from "@babackapp/ui/components/form";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { Label } from "@babackapp/ui/components/label";
import { AlertTriangleIcon, Plus, Trash2 } from "lucide-react";
import type { FieldPath } from "react-hook-form";
import {
  useWorkflowForm,
  type WorkflowsFormData,
} from "../../context/workflows-editor-workflows-context";
import { useNodeFormPath } from "../../hooks/use-node-form-path";
import {
  AVAILABLE_LOCALES,
  TranslatableField,
} from "@babackapp/ui/components/translatable-field";
import { BlockEditorInput } from "@babackapp/blocks-editor/block-editor-input";
import { useVariablesDictionary } from "@babackapp/variables-registry";
import { useWorkflowsEditorConfig } from "../../context/workflows-editor-config-context";
import useCurrentShopSuspense from "../../hooks/use-current-shop-suspense";
import { useEffect, useMemo } from "react";
import {
  getDomainDefaultLocale,
  getDomainLocales,
} from "../../lib/domain-locales";
import { getDefaultTemplatesForLocale } from "./default-templates";

/** Mirrors ActionNodeReturnZendeskCustomFieldDto. `label` is UI-only. */
type ZendeskCustomFieldRow = {
  /** null while the row is still being filled. */
  id: number | null;
  value: string;
  label?: string | null;
};

/**
 * A half-filled row is dropped by the engine, so the editor flags it — either
 * direction. A brand new, fully empty row is not: nothing is wrong yet.
 */
function isPartiallyFilled({ id, value }: ZendeskCustomFieldRow): boolean {
  const hasId = typeof id === "number";
  const hasValue = !!value;
  return hasId !== hasValue;
}

/**
 * Rendered only where the engine forwards the fields (see
 * supportsZendeskCustomFields). Ids are typed by hand: we wire no Zendesk
 * field-discovery API.
 */
function ZendeskCustomFields() {
  const { t } = useTranslation("trackoffice");
  const form = useWorkflowForm();
  const { variables } = useVariablesDictionary();
  const customFieldsPath = useNodeFormPath("config.customFields");

  if (!customFieldsPath) {
    return null;
  }

  const customFields = (form.watch(customFieldsPath) ??
    []) as ZendeskCustomFieldRow[];

  const setCustomFields = (rows: ZendeskCustomFieldRow[]) =>
    form.setValue(customFieldsPath, rows as never, { shouldDirty: true });

  const rowPath = (index: number, key: keyof ZendeskCustomFieldRow) =>
    `${customFieldsPath}.${index}.${key}` as FieldPath<WorkflowsFormData>;

  return (
    <div className="flex flex-col gap-2">
      <span className="font-medium text-xs text-muted-foreground">
        {t(
          "workflowsEditor.zendeskTicket.customFieldsLabel",
          "Ticket custom fields",
        )}
      </span>
      <p className="text-xs text-muted-foreground">
        {t(
          "workflowsEditor.zendeskTicket.customFieldsDescription",
          "Fill Zendesk custom fields on the created ticket. Enter the numeric field id found in your Zendesk admin — the value accepts variables.",
        )}
      </p>

      {customFields.map((row, index) => (
        <div
          key={index}
          className="flex flex-col gap-2 p-3 border rounded-lg bg-muted/30"
        >
          <div className="flex items-center justify-between">
            <Label className="text-xs">
              {t("workflowsEditor.zendeskTicket.customField", "Field")}{" "}
              {index + 1}
            </Label>
            <Button
              type="button"
              size="icon-xs"
              variant="ghost"
              onClick={() =>
                setCustomFields(customFields.filter((_, i) => i !== index))
              }
              className="h-6 w-6 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>

          <FormField
            control={form.control}
            name={rowPath(index, "id")}
            render={({ field }) => (
              <Input
                // Text, not type="number": its spinners eat the width a
                // 13-digit id needs, and it reports "" on intermediate states
                // like "1e", losing keystrokes.
                type="text"
                inputMode="numeric"
                value={(field.value as number | null) ?? ""}
                onChange={(event) => {
                  const digits = event.target.value.replace(/\D/g, "");
                  field.onChange(digits === "" ? null : Number(digits));
                }}
                className="h-8 text-xs"
                placeholder={t(
                  "workflowsEditor.zendeskTicket.customFieldIdPlaceholder",
                  "Zendesk field id",
                )}
              />
            )}
          />
          <FormField
            control={form.control}
            name={rowPath(index, "value")}
            render={({ field }) => (
              <BlockEditorInput
                variables={variables}
                value={(field.value as string) ?? ""}
                onChange={field.onChange}
                outputFormat="text"
                placeholder={t(
                  "workflowsEditor.zendeskTicket.customFieldValuePlaceholder",
                  "Value sent to Zendesk",
                )}
              />
            )}
          />
          <FormField
            control={form.control}
            name={rowPath(index, "label")}
            render={({ field }) => (
              <Input
                value={(field.value as string) ?? ""}
                onChange={field.onChange}
                className="h-8 text-xs"
                placeholder={t(
                  "workflowsEditor.zendeskTicket.customFieldLabelPlaceholder",
                  "Label (optional)",
                )}
              />
            )}
          />

          {isPartiallyFilled(row) && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <AlertTriangleIcon className="size-3" />
              {t(
                "workflowsEditor.zendeskTicket.customFieldIncomplete",
                "This field needs both an id and a value to be sent to Zendesk.",
              )}
            </p>
          )}
        </div>
      ))}

      <Button
        type="button"
        size="sm"
        variant="outline"
        className="self-start"
        onClick={() =>
          setCustomFields([...customFields, { id: null, value: "" }])
        }
      >
        <Plus className="size-3" />
        {t("workflowsEditor.zendeskTicket.addCustomField", "Add a field")}
      </Button>
    </div>
  );
}

export function PanelZendeskTicket() {
  const { t } = useTranslation("trackoffice");
  const form = useWorkflowForm();
  const { data: shop } = useCurrentShopSuspense();
  const { variables } = useVariablesDictionary();
  // The default texts reference domain variables ({{parcel.*}} vs {{return.*}}).
  const { domain, supportsZendeskCustomFields } = useWorkflowsEditorConfig();

  const titlePath = useNodeFormPath("config.title");
  const descriptionPath = useNodeFormPath("config.description");

  const availableLocales = useMemo(() => {
    return getDomainLocales(shop, domain).map((locale) => ({
      value: locale,
      label:
        AVAILABLE_LOCALES[locale as keyof typeof AVAILABLE_LOCALES]?.label ||
        locale,
      flag:
        AVAILABLE_LOCALES[locale as keyof typeof AVAILABLE_LOCALES]?.flag || "",
    }));
  }, [shop, domain]);

  const defaultLocale = getDomainDefaultLocale(shop, domain);

  useEffect(() => {
    if (!titlePath || !descriptionPath) return;

    const title = form.getValues(titlePath);
    const description = form.getValues(descriptionPath);

    const needsInitialization =
      !title ||
      Object.keys(title).length === 0 ||
      !description ||
      Object.keys(description).length === 0;

    if (needsInitialization) {
      const defaultTitle: Record<string, string> = {};
      const defaultDescription: Record<string, string> = {};

      availableLocales.forEach((locale) => {
        if (!title || !(title as Record<string, string>)[locale.value]) {
          const templates = getDefaultTemplatesForLocale(locale.value, domain);
          defaultTitle[locale.value] = templates.title;
        }
        if (
          !description ||
          !(description as Record<string, string>)[locale.value]
        ) {
          const templates = getDefaultTemplatesForLocale(locale.value, domain);
          defaultDescription[locale.value] = templates.description;
        }
      });

      if (!title || Object.keys(title).length === 0) {
        form.setValue(titlePath, defaultTitle, {
          shouldDirty: false,
        });
      }
      if (!description || Object.keys(description).length === 0) {
        form.setValue(descriptionPath, defaultDescription, {
          shouldDirty: false,
        });
      }
    }
  }, [titlePath, descriptionPath, availableLocales, form, domain]);

  if (!titlePath || !descriptionPath) {
    return null;
  }

  return (
    <PanelContent
      title={t("workflowsEditor.zendeskTicket.panelTitle", "Zendesk Ticket")}
    >
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground">
          {t(
            "workflowsEditor.zendeskTicket.description",
            "Customize the title and description of the Zendesk support ticket. You can use variables to insert dynamic data.",
          )}
        </p>

        <FormField
          control={form.control}
          name={titlePath}
          render={({ field }) => (
            <div className="flex flex-col gap-1">
              <span className="font-medium inline-flex gap-1 items-center text-muted-foreground text-xs">
                {t("workflowsEditor.zendeskTicket.title", "Ticket Title")}
              </span>
              <TranslatableField
                value={field.value as Record<string, string>}
                onChange={field.onChange}
                locales={availableLocales.map((l) => l.value)}
                defaultLocale={defaultLocale}
                placeholder={t(
                  "workflowsEditor.zendeskTicket.titlePlaceholder",
                  "Enter ticket title...",
                )}
                className="text-sm"
                renderInput={({ value, onChange, placeholder }) => (
                  <BlockEditorInput
                    variables={variables}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    outputFormat="text"
                  />
                )}
              />
            </div>
          )}
        />

        <FormField
          control={form.control}
          name={descriptionPath}
          render={({ field }) => (
            <div className="flex flex-col gap-1">
              <span className="font-medium inline-flex gap-1 items-center text-muted-foreground text-xs">
                {t(
                  "workflowsEditor.zendeskTicket.descriptionLabel",
                  "Ticket Description",
                )}
              </span>
              <TranslatableField
                value={field.value as Record<string, string>}
                onChange={field.onChange}
                locales={availableLocales.map((l) => l.value)}
                defaultLocale={defaultLocale}
                placeholder={t(
                  "workflowsEditor.zendeskTicket.descriptionPlaceholder",
                  "Enter ticket description...",
                )}
                className="text-sm"
                renderInput={({ value, onChange, placeholder }) => (
                  <BlockEditorInput
                    variables={variables}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    outputFormat="text"
                    className="min-h-[100px]"
                  />
                )}
              />
            </div>
          )}
        />

        {supportsZendeskCustomFields && <ZendeskCustomFields />}
      </div>
    </PanelContent>
  );
}
